import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Boutique = () => {
  const { lang, t } = useLang();
  const [works, setWorks] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/artworks`).then((res) => setWorks(res.data)).catch(() => setWorks([]));
  }, []);

  const categories = useMemo(
    () => [...new Set(works.map((w) => w.category).filter(Boolean))],
    [works]
  );
  const filtered = filter === "all" ? works : works.filter((w) => w.category === filter);

  const enquire = (work) => {
    window.dispatchEvent(new CustomEvent("mas:enquire", { detail: work }));
    if (window.__lenis) {
      window.__lenis.scrollTo("#contact", { duration: 1.5 });
    } else {
      document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="boutique" data-testid="boutique-section" className="mx-auto max-w-[1600px] px-4 py-24 sm:px-8 lg:px-16 lg:py-36">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">{t.boutique.kicker}</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 font-serif text-3xl font-light text-parchment sm:text-4xl lg:text-5xl">
          {t.boutique.title}
        </h2>
      </Reveal>
      <Reveal delay={0.18}>
        <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-sand sm:text-lg">{t.boutique.sub}</p>
      </Reveal>

      <Reveal delay={0.24}>
        <div className="mt-10 flex flex-wrap gap-3">
          <button
            data-testid="boutique-filter-all"
            onClick={() => setFilter("all")}
            className={`rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] transition-all duration-300 ${
              filter === "all" ? "border-gold bg-gold text-ink" : "border-gold/25 text-sand hover:border-gold/60 hover:text-parchment"
            }`}
          >
            {t.boutique.all}
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`boutique-filter-${cat}`}
              onClick={() => setFilter(cat)}
              className={`rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] transition-all duration-300 ${
                filter === cat ? "border-gold bg-gold text-ink" : "border-gold/25 text-sand hover:border-gold/60 hover:text-parchment"
              }`}
            >
              {t.boutique.categories[cat] || cat}
            </button>
          ))}
        </div>
      </Reveal>

      <motion.div layout className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <AnimatePresence mode="popLayout">
          {filtered.map((work) => (
            <motion.article
              layout
              key={work.id}
              data-testid="artwork-card"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="group relative flex flex-col overflow-hidden border border-gold/10 bg-card2 transition-colors duration-500 hover:border-gold/50"
            >
              <Link to={`/oeuvres/${work.id}`} data-testid="artwork-card-link" className="relative block aspect-square overflow-hidden">
                <img
                  src={work.image}
                  alt={lang === "fr" ? work.title_fr : work.title_en}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-108"
                  style={{ transitionProperty: "transform" }}
                />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_40%,rgba(13,12,10,0.35)_100%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <span className="absolute bottom-3 left-3 font-mono text-[9px] uppercase tracking-[0.25em] text-parchment opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  {t.boutique.view_work}
                </span>
              </Link>
              <div className="flex flex-1 flex-col p-5">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold/90">
                  {lang === "fr" ? work.medium_fr : work.medium_en || work.medium_fr}
                </p>
                <Link to={`/oeuvres/${work.id}`} className="mt-2 block">
                  <h3 className="font-serif text-2xl font-light leading-tight text-parchment transition-colors duration-300 hover:text-goldlight">
                    {lang === "fr" ? work.title_fr : work.title_en}
                  </h3>
                </Link>
                <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-sand/70">
                  {[work.year, work.dimensions].filter(Boolean).join(" · ")}
                </p>
                <div className="mt-4 flex items-center justify-between border-t border-gold/15 pt-4">
                  <span className="font-serif text-xl italic text-gold">
                    {work.price > 0 ? `${work.price} €` : t.boutique.on_request}
                  </span>
                  <button
                    data-testid="artwork-enquire-button"
                    onClick={() => enquire(work)}
                    className="rounded-full border border-gold/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
                  >
                    {t.boutique.enquire}
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};

export default Boutique;
